import React, { useRef, useState } from "react";
import Swal from "sweetalert2";
import "bootstrap/dist/css/bootstrap.min.css";
import loadSCGif from "../../../assets/load_SC.gif";
import {
  Table,
  TableContainer,
  TableData,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/Table/styles";
import Button from "../../../components/Button/button";
import { Colors } from "../../../shared/colors/colors";
import {
  FilterSearchDadosFinanceirosDto,
  FilterSearchDadosFinanceirosForReportDto,
  GetDadosFinanceirosDto,
  GetDadosFinanceirosReajusteDto,
  ReajusteValoresFinanceirosDto,
  RelatorioComparacaoValores,
} from "../../../shared/dtos/DadosFinanceiros.dto";
import dadosFinanceirosService from "../../../services/DadosFinanceirosService";
import { GrayTitle, StyledHR } from "../../../shared/styles/global.styles";
import {
  ContainerButtons,
  EmptySearchContainer,
  EmptySearchMessage,
  LoadingContainer,
  MainContainer,
  TitleAndButton,
} from "./styles";
import { useAuth } from "../../../AuthContext/AuthContext";
import { ModulosEnum } from "../../../shared/enum/modulos.enum";
import { PermissoesEnum } from "../../../shared/enum/permicao.enum";
import SearchModal from "./SearchModal/SearchModal";
import Pagination from "../../../components/Pagination/pagination";
import { formatDateToBR } from "../../../utils/dateUtils/dateUtils";
import { LoadingGif } from "./styles";
import { pdf } from "@react-pdf/renderer";
import RelatorioPDF from "./RelatorioPDF";

const RelatorioComparacaoValoresDadosFinanceiros = () => {
  const wrapperRef = useRef<HTMLDivElement>(null);

  const [showSearchModal, setShowSearchModal] = useState<boolean>(false);

  const [page, setPage] = useState<number>(1);
  const [totalDadosFinanceiros, setTotalDadosFinanceiros] = useState<number>(0);

  const take = 10;
  const totalPagesDadosFinanceiros = Math.ceil(totalDadosFinanceiros / take);

  const [paramsSearch, setParamsSearch] =
    useState<FilterSearchDadosFinanceirosForReportDto>({
      dataInicioInlusao: "",
      dataFimInclusao: "",
      lowerValues: true,
    });

  const [dadosFinanceiros, setDadosFinanceiros] = useState<
    RelatorioComparacaoValores[]
  >([]);
  const [isRequestDadosFinanceiros, setIsRequestDadosFinanceiros] =
    useState<boolean>(false);

  const [PDF, setPDF] = useState<Blob>();

  const handlePageChange = (pageNumber: number) => {
    if (pageNumber !== page) {
      setPage(pageNumber);
    }
  };

  const gerarRelatorioComparacaoValores = async () => {
    setIsRequestDadosFinanceiros(true);
    try {
      if (
        paramsSearch.dataInicioInlusao !== null &&
        paramsSearch.dataFimInclusao !== null
      ) {
        await dadosFinanceirosService
          .gerarRelatorioComparacaoValores(paramsSearch)
          .then((response) => {
            setDadosFinanceiros(response);
            setTotalDadosFinanceiros(response.length);
            Swal.fire({
              icon: "success",
              title: "Sucesso",
              text: "Relatório gerado com sucesso!",
              showCancelButton: true,
              confirmButtonText: "Imprimir",
              cancelButtonText: "Fechar",
              allowOutsideClick: false,
            }).then((result) => {
              if (result.isConfirmed) {
                gerarEImprimirPDF(response);
              }
            });
          })
          .catch((error) => {
            console.error("Erro ao buscar dados financeiros:", error);
            Swal.fire({
              title: "Erro",
              text: "Ocorreu um erro ao buscar os dados financeiros. Por favor, tente novamente.",
              icon: "error",
              confirmButtonText: "Fechar",
              allowOutsideClick: false,
            });
          });
      }
    } catch (error) {
      console.error("Erro ao buscar dados financeiros:", error);
      Swal.fire({
        title: "Erro",
        text: "Ocorreu um erro ao buscar os dados financeiros. Por favor, tente novamente.",
        icon: "error",
        confirmButtonText: "Fechar",
        allowOutsideClick: false,
      });
    }
    setIsRequestDadosFinanceiros(false);
  };

  const formatarValorEmReais = (valor: string) => {
    if (!valor) return "R$ 0,00";

    // Se o valor já estiver no formato numérico com ponto como separador decimal, converte diretamente para número
    const numero = parseFloat(valor.replace(",", "."));

    // Verifica se o valor é um número válido
    if (isNaN(numero)) return "R$ 0,00";

    // Formata o número para reais com separadores corretos
    return numero.toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });
  };

  const limparResultados = () => {
    setDadosFinanceiros([]);
    setParamsSearch({
      dataInicioInlusao: "",
      dataFimInclusao: "",
      lowerValues: true,
    });
    setPDF(undefined);
    setPage(1);
  };

  const gerarEImprimirPDF = async (dados: RelatorioComparacaoValores[]) => {
    try {
      if (!dados || dados.length === 0) {
        console.error("Tentativa de imprimir com dados vazios");
        Swal.fire({
          icon: "info",
          title: "Informação",
          text: "Não há dados para imprimir. Por favor, gere o relatório novamente.",
          allowOutsideClick: false,
        });
        return;
      }

      // Garantir que estamos usando as datas corretas para o PDF
      // Se não tivermos as datas aplicadas, usar as selecionadas no momento da geração
      const dataInicio = paramsSearch.dataInicioInlusao;
      const dataFim = paramsSearch.dataFimInclusao;

      if (!dataInicio || !dataFim) {
        console.warn("Datas de início ou fim estão faltando");
      }
      Swal.fire({
        title: "Gerando PDF do relatório...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      if (PDF) {
        const url = URL.createObjectURL(PDF);
        window.open(url);
        Swal.close();
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, 1000));

      await pdf(
        <RelatorioPDF
          data={dados}
          lotacao={paramsSearch.lotacao?.descricao}
          localPagamento={paramsSearch.localPagamento?.descricao}
          dateRange={{
            inicio: dataInicio || "",
            fim: dataFim || "",
          }}
        />
      )
        .toBlob()
        .then((blob) => {
          const url = URL.createObjectURL(blob);
          window.open(url);
          setPDF(blob);
        })
        .finally(() => {
          Swal.close();
        });
    } catch (error) {
      console.error("Erro ao gerar PDF:", error);
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Ocorreu um erro ao gerar o PDF para impressão.",
        allowOutsideClick: false,
      });
    }
  };

  const imprimirRelatorio = async () => {
    if (!dadosFinanceiros || !dadosFinanceiros.length) {
      Swal.fire({
        icon: "info",
        title: "Informação",
        text: "Não há dados para imprimir. Por favor, gere o relatório primeiro.",
        allowOutsideClick: false,
      });
      return;
    }

    gerarEImprimirPDF(dadosFinanceiros);
  };

  return (
    <MainContainer>
      <TitleAndButton>
        <GrayTitle>
          Relatório de Comparação de Valores dos Dados Financeiros
        </GrayTitle>
        <ContainerButtons ref={wrapperRef}>
          {dadosFinanceiros.length > 0 && (
            <>
              <Button
                schemaButton="full-primary"
                action={() => {
                  imprimirRelatorio();
                }}
                disabled={isRequestDadosFinanceiros}
              >
                Imprimir
              </Button>
              <Button
                schemaButton="full-primary"
                action={limparResultados}
                disabled={isRequestDadosFinanceiros}
              >
                Limpar resultados
              </Button>
            </>
          )}
          {dadosFinanceiros.length <= 0 && (
            <>
              <Button
                schemaButton="full-primary"
                action={() => {
                  setShowSearchModal(true);
                }}
              >
                Gerar relatório
              </Button>
            </>
          )}
        </ContainerButtons>
      </TitleAndButton>
      <StyledHR />

      {dadosFinanceiros ? (
        <>
          <TableContainer>
            {isRequestDadosFinanceiros ? (
              <LoadingContainer>
                <LoadingGif src={loadSCGif} alt="Carregando..." />
              </LoadingContainer>
            ) : (
              <Table id="dados-financeiros-tabela">
                <TableHead>
                  <TableRow
                    style={{
                      backgroundColor: Colors.primary,
                      color: Colors.white,
                    }}
                  >
                    <TableHeader>Contribuinte</TableHeader>
                    <TableHeader>Data de Inclusão</TableHeader>
                    <TableHeader>Dt.Incl.Dados Financeiros</TableHeader>
                    <TableHeader>Dt.Excl. Dados Financeiros</TableHeader>
                    <TableHeader>Lotação</TableHeader>
                    <TableHeader>Valor titular</TableHeader>
                    <TableHeader>Valor dependente</TableHeader>
                  </TableRow>
                </TableHead>
                <tbody>
                  {dadosFinanceiros && dadosFinanceiros.length > 0 ? (
                    dadosFinanceiros
                      .slice((page - 1) * take, page * take)
                      .map((dados, idx) => (
                        <React.Fragment key={`dados-financeiros-${idx}`}>
                          <TableRow>
                            {/* Contribuinte */}
                            <TableData
                              style={{
                                fontWeight: "normal",
                                color: "red",
                              }}
                            >
                              {dados.previous.contribuinte?.nome || "-"}
                            </TableData>

                            {/* Data de Inclusão */}
                            <TableData
                              style={{
                                fontWeight: "normal",
                                color: "red",
                              }}
                            >
                              {dados.previous.contribuinte?.movimentacoes?.[0]
                                ?.data_movimentacao
                                ? formatDateToBR(
                                    dados.previous.contribuinte.movimentacoes[0]
                                      .data_movimentacao
                                  )
                                : "-"}
                            </TableData>

                            {/* Dt.Incl.Dados Financeiros */}
                            <TableData
                              style={{
                                fontWeight: "normal",
                                color: "red",
                              }}
                            >
                              {dados.previous.dataInclusao
                                ? formatDateToBR(dados.previous.dataInclusao)
                                : "-"}
                            </TableData>

                            {/* Dt.Excl. Dados Financeiros */}
                            <TableData
                              style={{
                                fontWeight: "normal",
                                color: "red",
                              }}
                            >
                              {dados.previous.dataExclusao
                                ? formatDateToBR(dados.previous.dataExclusao)
                                : "-"}
                            </TableData>

                            {/* Lotação */}
                            <TableData
                              style={{
                                fontWeight: "normal",
                                color: "red",
                              }}
                            >
                              {dados.previous.lotacao?.descricao || "-"}
                            </TableData>

                            {/* Valor titular */}
                            <TableData
                              style={{
                                fontWeight: "normal",
                                color: "red",
                              }}
                            >
                              {dados.previous.valorTitular
                                ? formatarValorEmReais(
                                    dados.previous.valorTitular
                                  )
                                : "-"}
                            </TableData>

                            {/* Valor dependente */}
                            <TableData
                              style={{
                                fontWeight: "normal",
                                color: "red",
                              }}
                            >
                              {dados.previous.valorDependente
                                ? formatarValorEmReais(
                                    dados.previous.valorDependente
                                  )
                                : "-"}
                            </TableData>
                          </TableRow>

                          <TableRow>
                            {/* Contribuinte */}
                            <TableData
                              style={{
                                fontWeight: "bold",
                                color: "green",
                              }}
                            >
                              {dados.current.contribuinte?.nome || "-"}
                            </TableData>

                            {/* Data de Inclusão */}
                            <TableData
                              style={{
                                fontWeight: "bold",
                                color: "green",
                              }}
                            >
                              {dados.current.contribuinte?.movimentacoes?.[0]
                                ?.data_movimentacao
                                ? formatDateToBR(
                                    dados.current.contribuinte.movimentacoes[0]
                                      .data_movimentacao
                                  )
                                : "-"}
                            </TableData>

                            {/* Dt.Incl.Dados Financeiros */}
                            <TableData
                              style={{
                                fontWeight: "bold",
                                color: "green",
                              }}
                            >
                              {dados.current.dataInclusao
                                ? formatDateToBR(dados.current.dataInclusao)
                                : "-"}
                            </TableData>

                            {/* Dt.Excl. Dados Financeiros */}
                            <TableData
                              style={{
                                fontWeight: "bold",
                                color: "green",
                              }}
                            >
                              {dados.current.dataExclusao
                                ? formatDateToBR(dados.current.dataExclusao)
                                : "-"}
                            </TableData>

                            {/* Lotação */}
                            <TableData
                              style={{
                                fontWeight: "bold",
                                color: "green",
                              }}
                            >
                              {dados.current.lotacao?.descricao || "-"}
                            </TableData>

                            {/* Valor titular */}
                            <TableData
                              style={{
                                fontWeight: "bold",
                                color: "green",
                              }}
                            >
                              {dados.current.valorTitular
                                ? formatarValorEmReais(
                                    dados.current.valorTitular
                                  )
                                : "-"}
                            </TableData>

                            {/* Valor dependente */}
                            <TableData
                              style={{
                                fontWeight: "bold",
                                color: "green",
                              }}
                            >
                              {dados.current.valorDependente
                                ? formatarValorEmReais(
                                    dados.current.valorDependente
                                  )
                                : "-"}
                            </TableData>
                          </TableRow>

                          <TableRow>
                            <TableData
                              colSpan={7}
                              style={{ padding: "0.5rem" }}
                            />
                          </TableRow>
                        </React.Fragment>
                      ))
                  ) : (
                    <TableRow>
                      <TableData colSpan={7} className="text-center text-muted">
                        Não foi encontrado nenhum dado financeiro
                      </TableData>
                    </TableRow>
                  )}
                </tbody>
              </Table>
            )}
          </TableContainer>
          {dadosFinanceiros &&
            dadosFinanceiros.length > 0 &&
            totalPagesDadosFinanceiros >= 2 && (
              <Pagination
                currentPage={page}
                totalPages={totalPagesDadosFinanceiros}
                onPageChange={handlePageChange}
              />
            )}
        </>
      ) : (
        <EmptySearchContainer>
          <EmptySearchMessage>
            Por favor, utilize "Gerar relatório".
          </EmptySearchMessage>
        </EmptySearchContainer>
      )}
      {showSearchModal && (
        <SearchModal
          show={showSearchModal}
          paramsSearch={paramsSearch}
          setParamsSearch={setParamsSearch}
          onClose={() => setShowSearchModal(false)}
          onSubmit={() => gerarRelatorioComparacaoValores()}
        />
      )}
    </MainContainer>
  );
};

export default RelatorioComparacaoValoresDadosFinanceiros;
